import { useCallback, useState } from 'react'
import { useAppSelector } from '@store/hooks'
import {
  type AiFeatureKey,
  normalizeAiFeatureKey,
  postAiCompletionApi,
} from '@services/api'
import { logger } from '@utils/safeLogger'
export const aiTextActions: {
  key: AiFeatureKey
  label: string
  icon: string
}[] = [
  { key: 'rewrite', label: 'Rewrite', icon: 'ti ti-refresh' },
  { key: 'formalize', label: 'Formalize', icon: 'ti ti-writing' },
  { key: 'summarize', label: 'Summarize', icon: 'ti ti-list' },
  { key: 'list_items', label: 'List items', icon: 'ti ti-list-numbers' },
  { key: 'simplify', label: 'Simplify', icon: 'ti ti-vocabulary' },
  { key: 'spelling_check', label: 'Spelling check', icon: 'ti ti-abc' },
  { key: 'grammar_check', label: 'Grammar check', icon: 'ti ti-language' },
]

interface UseAiTextAssistResult {
  isLoading: boolean
  result: string | null
  error: string | null
  run: (
    userText: string,
    featureKey: AiFeatureKey | string,
    options?: {
      promptTemplate?: string
      systemPrompt?: string
    },
  ) => Promise<string | null>
  reset: () => void
}

export function useAiTextAssist(): UseAiTextAssistResult {
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)

  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const run = useCallback(
    async (
      userText: string,
      featureKey: AiFeatureKey | string,
      options?: {
        promptTemplate?: string
        systemPrompt?: string
      },
    ): Promise<string | null> => {
      const clinicId = selectedClinic?.clinic_id
      if (!clinicId) {
        const msg = 'Select a clinic before using AI features.'
        logger.error('AI text assist skipped', { featureKey, reason: msg })
        setError(msg)
        return null
      }

      const user = JSON.parse(localStorage.getItem('user') || '{}')
      if (!user.user_id) {
        const msg = 'User session not found.'
        logger.error('AI text assist skipped', { featureKey, reason: msg })
        setError(msg)
        return null
      }

      setIsLoading(true)
      setError(null)
      setResult(null)

      const normalizedFeatureKey = normalizeAiFeatureKey(featureKey)
      const response = await postAiCompletionApi({
        clinic_id: clinicId,
        user_text: userText,
        feature_key: normalizedFeatureKey,
        ...(options?.promptTemplate?.trim()
          ? { prompt_template: options.promptTemplate.trim() }
          : {}),
        ...(options?.systemPrompt?.trim()
          ? { system_prompt: options.systemPrompt.trim() }
          : {}),
        user_id: user.user_id,
      })

      setIsLoading(false)

      if (response.statusCode === 200 && response.data?.content) {
        setResult(response.data.content)
        return response.data.content
      }

      const msg = response.message || 'AI request failed.'
      logger.error('AI text assist failed', {
        featureKey: normalizedFeatureKey,
        statusCode: response.statusCode,
        message: msg,
        hasContent: Boolean(response.data?.content),
      })
      setError(msg)
      return null
    },
    [selectedClinic],
  )

  const reset = useCallback((): void => {
    setResult(null)
    setError(null)
    setIsLoading(false)
  }, [])

  return { isLoading, result, error, run, reset }
}
